import {
  AppCard,
  PageContainer,
  RouteTabLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { TabContext, TabPanel } from "@mui/lab";
import { Box, CircularProgress, Tabs } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  useGetGeneralDocument,
  useListGeneralDocuments,
} from "../../../../__generated__/orval/endpoints/general-documents/general-documents";
import { useListGuides } from "../../../../__generated__/orval/endpoints/guides/guides";
import { GeneralDocumentCard } from "./-internal_components/GeneralDocumentCard";
import { GuidesList } from "./-internal_components/GuidesList";

const searchSchema = z.object({
  page: z.optional(z.coerce.number()).default(1).catch(1),
  tab: z
    .enum(["checklist", "guides", "localContent"])
    .default("checklist")
    .catch("checklist"),
});

type TabType = z.infer<typeof searchSchema.shape.tab>;

const RouteComponent: React.FC = () => {
  const navigate = Route.useNavigate();
  const { t } = useTranslation();
  const { page, tab } = Route.useSearch();

  const { data: checklistResponse, isLoading: isLoadingChecklist } =
    useListGuides(
      {
        kind: "Checklist",
        page,
      },
      {
        query: {
          enabled: tab === "checklist",
        },
      },
    );

  const {
    data: localContentDocumentsResponse,
    isLoading: isLoadingLocalContentDocuments,
  } = useListGeneralDocuments(
    { kind: "local_content" },
    {
      query: {
        enabled: tab === "localContent",
      },
    },
  );

  const { data: importGuideline, isLoading: isLoadingImportGuideline } =
    useGetGeneralDocument("import_guideline", {
      query: {
        enabled: tab === "guides",
      },
    });

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: TabType) => {
      navigate({
        search: (prev) => ({
          ...prev,
          tab: newValue,
        }),
      });
    },
    [navigate],
  );

  if (
    isLoadingChecklist ||
    isLoadingLocalContentDocuments ||
    isLoadingImportGuideline
  ) {
    return (
      <PageContainer title={t("common.guides")}>
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            height: "400px",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer title={t("common.guides")}>
      <AppCard>
        <TabContext value={tab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs onChange={handleTabChange} value={tab}>
              <RouteTabLink
                label={t("common.checklist")}
                search={(prev) => ({ ...prev, page: 1, tab: "checklist" })}
                to="/vbuyer/guides"
                value="checklist"
              />
              <RouteTabLink
                label={t("common.localContent")}
                search={(prev) => ({ ...prev, page: 1, tab: "localContent" })}
                to="/vbuyer/guides"
                value="localContent"
              />
              <RouteTabLink
                label={t("common.guides")}
                search={(prev) => ({ ...prev, page: 1, tab: "guides" })}
                to="/vbuyer/guides"
                value="guides"
              />
            </Tabs>
          </Box>

          <TabPanel sx={{ padding: 0 }} value="checklist">
            <GuidesList
              guides={checklistResponse?.results}
              pagesCount={checklistResponse?.pagination.total_pages}
            />
          </TabPanel>

          <TabPanel sx={{ padding: 2 }} value="localContent">
            {localContentDocumentsResponse?.results.length ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {localContentDocumentsResponse.results.map((document) => (
                  <GeneralDocumentCard
                    document={document}
                    key={document.path}
                  />
                ))}
              </Box>
            ) : (
              <GeneralDocumentCard
                notAvailableMessage={t("common.noDocumentsAvailable")}
              />
            )}
          </TabPanel>

          <TabPanel sx={{ padding: 2 }} value="guides">
            <GeneralDocumentCard
              document={importGuideline}
              notAvailableMessage={t("common.noDocumentsAvailable")}
            />
          </TabPanel>
        </TabContext>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute("/_private/vbuyer/guides/")({
  component: RouteComponent,
  validateSearch: searchSchema,
});
